import { type RefObject, useEffect } from 'react';
import { isNil } from 'lodash/fp';

export const LEFT_CLICK = 'click';
export const RIGHT_CLICK = 'contextmenu';

interface UseOutsideClickHandlerOptions {
    enabled?: boolean;
    useCapture?: boolean;
    allowDetachedTargets?: boolean;
}

export function useOutsideClickHandler(
    ref: RefObject<HTMLElement>,
    handler: (event: MouseEvent) => void,
    {
        enabled = false,
        useCapture = false,
        allowDetachedTargets = false
    }: UseOutsideClickHandlerOptions = {}
) {
    useEffect(() => {
        if (!enabled || isNil(ref) || isNil(ref.current)) {
            return;
        }

        function listener(event: MouseEvent) {
            const targetElement = event.target as HTMLElement;
            const targetIsClickThrough = targetElement.hasAttribute('data-click-through');

            if (!allowDetachedTargets && !document.body.contains(targetElement)) {
                return;
            }

            if (ref.current?.contains(targetElement) && !targetIsClickThrough) {
                return;
            }

            handler(event);
        }

        // useCapture is used to prioritize this listener.
        // primarily it can be used as an escape hatch for when a component is outside of
        // any existing (DOM) hierarchy, meaning that they do not live in for instance
        // the routing or store lifecycle, or in modal container, or even deeply nested in the DOM.
        // This should only be used if a component is isolated and/or does not interact
        // with other components not contained by it.
        document.addEventListener(LEFT_CLICK, listener, useCapture);
        document.addEventListener(RIGHT_CLICK, listener, useCapture);

        return () => {
            document.removeEventListener(LEFT_CLICK, listener, useCapture);
            document.removeEventListener(RIGHT_CLICK, listener, useCapture);
        };
    }, [ref, handler, enabled, useCapture, allowDetachedTargets]);
}
